/**
 * AbbruchGruendeSettings — CRUD-Verwaltung der Anfrage-Abbruch-Gründe
 * (Einstellungen → Abbruch-Gründe), nach dem Muster von BkStagesSettings.
 *
 * Gründe werden im Abbruch-Dialog der Anfragen-Seite angeboten. Gründe mit
 * "Beschreibung erforderlich" (Basis: "Sonstiges") erzwingen dort einen
 * Freitext. Endpoints: GET/POST/PUT/DELETE /v2/admin/anfrage-abbruch-gruende.
 *
 * Getrennte Kataloge für PB- und BK-Anfragen (scope pb|bk) — umschaltbar
 * über die Tabs oben; der Abbruch-Dialog zeigt nur die Gründe der Rolle.
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, SectionTitle, Button, Field, Input, Badge } from '../../components/ui';
import { getThemeColors, BRAND, BRAND_LIGHT, BRAND_DARK } from '../../lib/theme';
import { v2AbbruchGruende } from '../../lib/v1';
import type { AbbruchGrund, AbbruchScope } from '../../lib/v1';

interface AbbruchGruendeSettingsProps {
  dark: boolean;
}

interface EditState {
  titel: string;
  beschreibung: string;
  requires_beschreibung: boolean;
  is_active: boolean;
}

const EMPTY_STATE: EditState = { titel: '', beschreibung: '', requires_beschreibung: false, is_active: true };

export function AbbruchGruendeSettings({ dark }: AbbruchGruendeSettingsProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [scope, setScope] = useState<AbbruchScope>('pb');

  const tabs = (
    <div role="tablist" style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
      {(['pb', 'bk'] as const).map(s => {
        const on = s === scope;
        return (
          <button
            key={s}
            role="tab"
            aria-selected={on}
            onClick={() => setScope(s)}
            style={{
              border: `1px solid ${on ? BRAND : t.border}`, borderRadius: 8,
              padding: '7px 14px', cursor: 'pointer', fontSize: 13, fontWeight: on ? 600 : 500,
              background: on ? BRAND_LIGHT : t.card, color: on ? BRAND_DARK : t.text,
              fontFamily: 'Outfit, sans-serif',
            }}
          >{tr(`settings.abbruch_gruende.scope.${s}`)}</button>
        );
      })}
    </div>
  );

  // key={scope}: beim Tab-Wechsel frischer State (Formulare, Liste).
  return <AbbruchGruendeList key={scope} dark={dark} scope={scope} tabs={tabs} />;
}

function AbbruchGruendeList({ dark, scope, tabs }: { dark: boolean; scope: AbbruchScope; tabs: React.ReactNode }) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();

  const [gruende, setGruende] = useState<AbbruchGrund[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [editId, setEditId] = useState<number | null>(null);
  const [editState, setEditState] = useState<EditState>(EMPTY_STATE);
  const [editError, setEditError] = useState<string | null>(null);
  const [editSaving, setEditSaving] = useState(false);

  const [showAddForm, setShowAddForm] = useState(false);
  const [addState, setAddState] = useState<EditState>(EMPTY_STATE);
  const [addError, setAddError] = useState<string | null>(null);
  const [addSaving, setAddSaving] = useState(false);

  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      // all=true: Einstellungen zeigen auch inaktive Gründe.
      const data = await v2AbbruchGruende.list(true, scope);
      setGruende(data.slice().sort((a, b) => a.sort - b.sort || a.id - b.id));
    } catch {
      setLoadError(tr('settings.abbruch_gruende.load_error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Up/Down: sort-Werte der beiden Nachbarn tauschen (2 Updates).
  const swap = async (idx: number, dir: -1 | 1) => {
    const other = idx + dir;
    if (other < 0 || other >= gruende.length) return;
    const a = gruende[idx];
    const b = gruende[other];
    const next = [...gruende];
    [next[idx], next[other]] = [b, a];
    setGruende(next);
    try {
      await Promise.all([
        v2AbbruchGruende.update(a.id, { sort: b.sort }),
        v2AbbruchGruende.update(b.id, { sort: a.sort }),
      ]);
      void load();
    } catch {
      void load();
    }
  };

  const openEdit = (g: AbbruchGrund) => {
    setEditId(g.id);
    setEditState({
      titel: g.titel,
      beschreibung: g.beschreibung ?? '',
      requires_beschreibung: g.requires_beschreibung,
      is_active: g.is_active,
    });
    setEditError(null);
  };

  const saveEdit = async () => {
    if (editId === null) return;
    if (!editState.titel.trim()) {
      setEditError(tr('settings.abbruch_gruende.error.title_required'));
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      const updated = await v2AbbruchGruende.update(editId, {
        titel: editState.titel.trim(),
        beschreibung: editState.beschreibung || null,
        requires_beschreibung: editState.requires_beschreibung,
        is_active: editState.is_active,
      });
      setGruende(prev => prev.map(g => g.id === editId ? updated : g));
      setEditId(null);
    } catch {
      setEditError(tr('settings.abbruch_gruende.error.save'));
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    setDeleteError(null);
    try {
      await v2AbbruchGruende.remove(id);
      setGruende(prev => prev.filter(g => g.id !== id));
    } catch {
      setDeleteError(tr('settings.abbruch_gruende.error.delete'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleAdd = async () => {
    if (!addState.titel.trim()) {
      setAddError(tr('settings.abbruch_gruende.error.title_required'));
      return;
    }
    setAddSaving(true);
    setAddError(null);
    try {
      const created = await v2AbbruchGruende.create({
        scope,
        titel: addState.titel.trim(),
        beschreibung: addState.beschreibung || null,
        requires_beschreibung: addState.requires_beschreibung,
        is_active: addState.is_active,
      });
      setGruende(prev => [...prev, created]);
      setAddState(EMPTY_STATE);
      setShowAddForm(false);
    } catch {
      setAddError(tr('settings.abbruch_gruende.error.create'));
    } finally {
      setAddSaving(false);
    }
  };

  const INPUT_STYLE: React.CSSProperties = {
    border: `1px solid ${t.border}`, borderRadius: 8,
    padding: '7px 10px', fontSize: 13, color: t.heading,
    background: t.inputBg, fontFamily: 'Outfit, sans-serif',
    outline: 'none', width: '100%',
  };

  const toggleButtons = (
    value: boolean,
    onChange: (v: boolean) => void,
    labels: [string, string],
  ) => (
    <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
      {[true, false].map(v => (
        <button
          key={String(v)}
          onClick={() => onChange(v)}
          style={{
            flex: 1, padding: '8px 0', borderRadius: 8, border: `1px solid ${value === v ? '#22c55e' : t.border}`,
            background: value === v ? '#ecfdf3' : t.card,
            color: value === v ? '#027a48' : t.text,
            cursor: 'pointer', fontSize: 12, fontWeight: 500, fontFamily: 'Outfit, sans-serif',
          }}
        >
          {v ? labels[0] : labels[1]}
        </button>
      ))}
    </div>
  );

  const editForm = (
    state: EditState,
    setState: React.Dispatch<React.SetStateAction<EditState>>,
    error: string | null,
    saving: boolean,
    onSave: () => void,
    onCancel: () => void,
    heading?: string,
  ) => (
    <div style={{
      padding: 14, background: t.cardMuted, border: `1px solid ${t.borderSoft}`,
      borderRadius: 10, marginBottom: 8, marginTop: heading ? 14 : 0,
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      {heading && (
        <div style={{ fontSize: 13, fontWeight: 600, color: t.heading, marginBottom: 4 }}>{heading}</div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field label={tr('settings.abbruch_gruende.field.title')}>
          <Input
            value={state.titel}
            onChange={e => setState(s => ({ ...s, titel: e.target.value }))}
            dark={dark}
            placeholder={tr('settings.abbruch_gruende.field.title_placeholder')}
          />
        </Field>
        <Field label={tr('settings.abbruch_gruende.field.description')}>
          <input
            value={state.beschreibung}
            onChange={e => setState(s => ({ ...s, beschreibung: e.target.value }))}
            style={INPUT_STYLE}
            placeholder={tr('settings.abbruch_gruende.field.description_placeholder')}
          />
        </Field>
        <Field label={tr('settings.abbruch_gruende.field.requires_beschreibung')}>
          {toggleButtons(
            state.requires_beschreibung,
            v => setState(s => ({ ...s, requires_beschreibung: v })),
            [tr('settings.abbruch_gruende.yes'), tr('settings.abbruch_gruende.no')],
          )}
        </Field>
        <Field label={tr('settings.abbruch_gruende.field.status')}>
          {toggleButtons(
            state.is_active,
            v => setState(s => ({ ...s, is_active: v })),
            [tr('settings.abbruch_gruende.active'), tr('settings.abbruch_gruende.inactive')],
          )}
        </Field>
      </div>

      {error && <div style={{ fontSize: 12, color: '#b42318' }}>{error}</div>}

      <div style={{ display: 'flex', gap: 8 }}>
        <Button variant="primary" size="sm" dark={dark} disabled={saving} onClick={onSave}>
          {saving ? tr('settings.abbruch_gruende.saving') : tr('settings.abbruch_gruende.save')}
        </Button>
        <Button variant="secondary" size="sm" dark={dark} onClick={onCancel}>
          {tr('settings.abbruch_gruende.cancel')}
        </Button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <Card dark={dark} style={{ padding: 20 }}>
        <div style={{ color: t.muted, fontSize: 13 }}>{tr('settings.abbruch_gruende.loading')}</div>
      </Card>
    );
  }

  if (loadError) {
    return (
      <Card dark={dark} style={{ padding: 20 }}>
        <div style={{ color: '#b42318', fontSize: 13, marginBottom: 12 }}>{loadError}</div>
        <Button variant="secondary" size="sm" dark={dark} onClick={() => void load()}>
          {tr('settings.abbruch_gruende.retry')}
        </Button>
      </Card>
    );
  }

  return (
    <Card dark={dark} style={{ padding: 20 }}>
      <SectionTitle
        dark={dark}
        title={tr('settings.abbruch_gruende.title')}
        subtitle={tr('settings.abbruch_gruende.subtitle')}
      />
      {tabs}

      {gruende.map((g, idx) => (
        <div key={g.id}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 0',
            borderBottom: `1px solid ${t.borderSoft}`,
            flexWrap: 'wrap',
          }}>
            <Badge tone="neutral" dark={dark}>{idx + 1}</Badge>

            <div style={{ flex: 1, minWidth: 100 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: t.heading }}>{g.titel}</div>
              {g.beschreibung && (
                <div style={{
                  fontSize: 11, color: t.muted, marginTop: 2,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260,
                }}>
                  {g.beschreibung}
                </div>
              )}
            </div>

            {g.requires_beschreibung && (
              <Badge tone="info" dark={dark}>{tr('settings.abbruch_gruende.requires_badge')}</Badge>
            )}

            <Badge tone={g.is_active ? 'success' : 'neutral'} dark={dark}>
              {g.is_active ? tr('settings.abbruch_gruende.active') : tr('settings.abbruch_gruende.inactive')}
            </Badge>

            <button
              aria-label={tr('settings.abbruch_gruende.up')}
              disabled={idx === 0}
              onClick={() => void swap(idx, -1)}
              style={{
                background: 'none', border: `1px solid ${t.border}`, borderRadius: 6,
                padding: '3px 8px', cursor: idx === 0 ? 'not-allowed' : 'pointer',
                color: idx === 0 ? t.muted : t.text, fontSize: 12,
                fontFamily: 'Outfit, sans-serif',
              }}
            >↑</button>
            <button
              aria-label={tr('settings.abbruch_gruende.down')}
              disabled={idx === gruende.length - 1}
              onClick={() => void swap(idx, 1)}
              style={{
                background: 'none', border: `1px solid ${t.border}`, borderRadius: 6,
                padding: '3px 8px', cursor: idx === gruende.length - 1 ? 'not-allowed' : 'pointer',
                color: idx === gruende.length - 1 ? t.muted : t.text, fontSize: 12,
                fontFamily: 'Outfit, sans-serif',
              }}
            >↓</button>

            <Button variant="secondary" size="sm" dark={dark} onClick={() => openEdit(g)}>
              {tr('settings.abbruch_gruende.edit')}
            </Button>

            <Button
              variant="secondary" size="sm" dark={dark}
              disabled={deletingId === g.id}
              onClick={() => void handleDelete(g.id)}
            >
              {deletingId === g.id ? '…' : tr('settings.abbruch_gruende.delete')}
            </Button>
          </div>

          {editId === g.id && editForm(
            editState, setEditState, editError, editSaving,
            () => void saveEdit(), () => { setEditId(null); setEditError(null); },
          )}
        </div>
      ))}

      {deleteError && (
        <div style={{ fontSize: 12, color: '#b42318', marginTop: 6 }}>{deleteError}</div>
      )}

      {showAddForm ? (
        editForm(
          addState, setAddState, addError, addSaving,
          () => void handleAdd(), () => { setShowAddForm(false); setAddError(null); },
          tr('settings.abbruch_gruende.new_heading'),
        )
      ) : (
        <div style={{ marginTop: 14 }}>
          <Button variant="primary" size="sm" dark={dark} onClick={() => setShowAddForm(true)}>
            {tr('settings.abbruch_gruende.add')}
          </Button>
        </div>
      )}
    </Card>
  );
}
